<template>
  <div class="purchase-order-content">
    <div class="left-content">
      <div class="purchase-order-details-container">
        <div class="purchase-order-details-container-header">
          <div class="title-text">
            {{ titleText }}
          </div>
          <div
            v-if="!loading && purchaseOrder"
            class="actions"
          >
            <LockingButton
              v-if="purchaseOrder.id > 0 && !purchaseOrder.submitted"
              class="submit-purchase-order"
              :locked="savingStateChange"
              :text="'Submit Purchase Order'"
              @click="onSubmitPurchaseOrderClicked"
            />
            <LockingButton
              v-if="purchaseOrder.id > 0 && purchaseOrder.submitted && !purchaseOrder.verified"
              class="verify-purchase-order"
              :locked="savingStateChange"
              :text="'Verify Purchase Order'"
              @click="onVerifyPurchaseOrderClicked"
            />
            <LockingButton
              v-if="purchaseOrder.id > 0 && purchaseOrder.canReceiveAll"
              class="receive-all"
              :locked="savingStateChange"
              :text="'Receive All'"
              @click="onReceiveAllPurchaseOrderItemsClicked"
            />
            <LockingButton
              class="save-button"
              :disabled="!isDirty"
              :locked="saving"
              :text="'Save'"
              @click="onSavePurchaseOrderClicked(false)"
            />
          </div>
        </div>
        <LoadingPulse
          :loading="loading"
          class="loading-spinner"
        />
        <div
          v-if="!loading && purchaseOrder"
          class="purchase-order-details-container-content"
        >
          <TextInput
            v-if="purchaseOrder.id > 0"
            v-model="purchaseOrder.poNumber"
            :disabled="true"
            label="P.O. Number"
            class="input"
          />
          <Dropdown
            v-if="!purchaseOrder || purchaseOrder.id === 0"
            v-model="purchaseOrder.supplierId"
            class="input"
            label="Supplier"
            :show-required="true"
            :options="supplierDropdownOptions"
          />
          <TextInput
            v-else
            v-model="supplierName"
            :disabled="true"
            label="Supplier"
            class="input"
          />
          <TextArea
            v-model="purchaseOrder.description"
            label="Description"
            class="input"
          />
          <TextArea
            v-model="purchaseOrder.setACalendarDate"
            label="Set A Calendar Date"
            class="input"
          />
          <TextInput
            v-model="purchaseOrder.venderPoNumber"
            label="Vender P.O. Number"
            class="input"
          />
          <div
            v-if="purchaseOrder.submitted && purchaseOrder.submittedByUser && purchaseOrder.submittedDate"
            class="submitted-info"
          >
            Submitted by {{ purchaseOrder.submittedByUser.firstName }} {{ purchaseOrder.submittedByUser.lastName }} {{ $methods.formatDate(purchaseOrder.submittedDate) }}
          </div>
          <div
            v-if="purchaseOrder.verified && purchaseOrder.verifiedByUser && purchaseOrder.verifiedDate"
            class="verified-info"
          >
            Verified by {{ purchaseOrder.verifiedByUser.firstName }} {{ purchaseOrder.verifiedByUser.lastName }} {{ $methods.formatDate(purchaseOrder.verifiedDate) }}
          </div>
          <AuditInformation
            v-if="purchaseOrder.id > 0 && purchaseOrder.createdByUser && purchaseOrder.createdDate && purchaseOrder.lastModifiedByUser && purchaseOrder.lastModifiedDate"
            class="audit-information"
            :created-by-user="purchaseOrder.createdByUser"
            :created-date="purchaseOrder.createdDate"
            :last-modified-by-user="purchaseOrder.lastModifiedByUser"
            :last-modified-date="purchaseOrder.lastModifiedDate"
          />
        </div>
      </div>
      <div class="notes-container">
        <div class="notes-container-header">
          <div class="notes-container-header-text">
            Notes
          </div>
          <a 
            v-show="!loading && purchaseOrder && purchaseOrder.id > 0"
            class="create-note"
            @click="onCreateNoteClicked"
          >
            Create
          </a>
        </div>
        <LoadingPulse
          :loading="loading"
          class="loading-spinner"
        />
        <div
          v-if="!loading && purchaseOrder"
        >
          <NoteTile
            v-for="note in purchaseOrder.notes"
            :key="note.id"
            :note="note"
            @delete="onDeleteNote"
            @edit="onEditNote"
          />
        </div>
        <div
          v-if="(!purchaseOrder || purchaseOrder.id === 0) && !loading"
          class="message"
        >
          * Notes can be added to the purchase order once it is initally created.
        </div>
      </div>
    </div>
    <div class="items-container">
      <div class="items-container-header">
        <div class="items-container-header-text">
          Items
        </div>
        <a
          v-show="!loading && purchaseOrder && purchaseOrder.canAddItems"
          class="add-item"
          @click="onAddItemClicked"
        >
          Add
        </a>
      </div>
      <LoadingPulse
        :loading="loading"
        class="loading-spinner"
      />
      <div
        v-if="!loading"
        class="overflow-wrapper"
      >
        <table>
          <thead>
            <tr>
              <th class="no-sort">
                <div>
                  Item
                </div>
              </th>
              <th class="no-sort">
                <div>
                  Use For Fulfillment?
                </div>
              </th>
              <th class="no-sort">
                <div>
                  For
                </div>
              </th>
              <th
                v-if="purchaseOrder && purchaseOrder.id > 0 && purchaseOrder.verified"
                class="no-sort"
              />
              <th 
                v-if="purchaseOrder && purchaseOrder.id > 0"
                class="no-sort"
              />
            </tr>
          </thead>
          <tbody v-if="purchaseOrder && purchaseOrder.purchaseOrderItems.length > 0">
            <tr
              v-for="purchaseOrderItem in purchaseOrder.purchaseOrderItems"
              :key="purchaseOrderItem.id"
              :class="{clickable: !purchaseOrder.verified}"
            >
              <td>{{ purchaseOrderItem.name }}</td>
              <td>
                <Checkbox
                  v-model="purchaseOrderItem.canBeUsedToFulfillOrderPart"
                  label="Yes"
                  :disabled="purchaseOrderItem.purchaseOrderItemFor !== undefined"
                  @update:modelValue="onPurchaseOrderItemChanged(purchaseOrderItem)"
                />
              </td>
              <td>
                <router-link
                  v-if="purchaseOrderItem.purchaseOrderItemFor"
                  aria-label="View Purchase Order (opens in a new window)"
                  :to="'/order/' + purchaseOrderItem.purchaseOrderItemFor.orderId"
                  target="_blank"
                >
                  {{ 'Order #' + purchaseOrderItem.purchaseOrderItemFor.orderId + ' for ' + purchaseOrderItem.purchaseOrderItemFor.customerName }}
                </router-link>
              </td>
              <td v-if="purchaseOrder.id > 0 && purchaseOrder.verified">
                <button
                  v-if="!purchaseOrderItem.receivedDate"
                  class="save-button"
                  @click="onReceivePurchaseOrderItemClicked(purchaseOrderItem)"
                >
                  Receive
                </button>
                <div
                  v-else
                  class="received-item"
                >
                  {{ receivedText(purchaseOrderItem) }}
                </div>
              </td>
              <td
                v-if="purchaseOrder.id > 0" 
                class="delete"
              >
                <TrashCanSvg
                  @click.stop="onDeletePurchaseOrderItemClicked(purchaseOrderItem.id)"
                />
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td
                v-if="purchaseOrder && purchaseOrder.id > 0"
                :colspan="purchaseOrder.verified ? 5 : 4"
                class="message"
              >
                There are no items on the purchase order. Please click 'Create' above to add an item.
              </td>
              <td
                v-else
                :colspan="3"
                class="message"
              >
                Items can be added to the purchase order once it is initally created.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <transition name="slide-up">
      <CreatePurchaseOrderItemModal
        v-if="showCreatePurchaseOrderItemModal && purchaseOrder && purchaseOrder.supplierId"
        :purchase-order-id="id"
        :supplier-id="purchaseOrder.supplierId"
        @purchase-order-item-created="getPurchaseOrder"
        @close="showCreatePurchaseOrderItemModal = false"
      />
    </transition>
    <transition name="slide-up">
      <NoteModal 
        v-if="showNoteModal && noteToEdit"
        :note="noteToEdit"
        :purchase-order-id="id"
        @note-changed="getPurchaseOrder"
        @close="showNoteModal = false"
      />
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject, PropType } from 'vue'
import TextArea from '@/components/shared/TextArea.vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import CreatePurchaseOrderItemModal from '@/components/modals/CreatePurchaseOrderItemModal.vue'
import NoteModal from '@/components/modals/NoteModal.vue'
import NoteTile from '@/components/elements/NoteTile.vue'
import { PurchaseOrderItemDetails } from '@/@types/responses/purchase_order/PurchaseOrderItemDetails'
import { PurchaseOrderDetails } from '@/@types/responses/purchase_order/PurchaseOrderDetails'
import { Note } from '@/@types/responses/note/Note'
import { PurchaseOrderService } from '@/services/purchaseOrder.service'
import { NoteService } from '@/services/note.service'
import { PurchaseOrderItemService } from '@/services/purchaseOrderItem.service'
import Dropdown from '@/components/shared/Dropdown.vue'
import { SupplierService } from '@/services/supplier.service'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'
import AuditInformation from '@/components/elements/AuditInformation.vue'
import { PurchaseOrderItemForDetails } from '@/@types/responses/purchase_order/PurchaseOrderItemForDetails'
import { InventoryItemService } from '@/services/inventoryItem.service'
import TrashCanSvg from '@/components/svgs/TrashCanSvg.vue'
import { UpdatePurchaseOrderMessage } from '@/@types/messages/UpdatePurchaseOrderMessage'
import { CreatePurchaseOrderMessage } from '@/@types/messages/CreatePurchaseOrderMessage'
import { UpdatePurchaseOrderItemMessage } from '@/@types/messages/UpdatePurchaseOrderItemMessage'
import { CustomerService } from '@/services/customer.service'
import { CustomerDetails } from '@/@types/responses/customer/CustomerDetails'
import Checkbox from '@/components/shared/Checkbox.vue'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'PurchaseOrder',
  components: {
    AuditInformation,
    Checkbox,
    CreatePurchaseOrderItemModal,
    Dropdown,
    LoadingPulse,
    LockingButton,
    NoteModal,
    NoteTile,
    TextArea,
    TextInput,
    TrashCanSvg
  },
  beforeRouteLeave() {
    if (this.showCreatePurchaseOrderItemModal || this.showNoteModal) {
      this.showCreatePurchaseOrderItemModal = false
      this.showNoteModal = false

      return false
    }

    return true
  },
  props: {
    id: {
      type: Number,
      required: true
    },
    customerId: {
      type: Number,
      default: 0
    },
    orderId: {
      type: Number,
      default: 0
    },
    orderPartIds: {
      type: Array as PropType<Array<number>>,
      default: []
    },
    orderPartName: {
      type: String,
      default: ''
    },
    orderPartPrice: {
      type: Number,
      default: 0
    }
  },
  setup() {
    const customerService = inject(PROVIDER_CONSTANTS.customerService) as CustomerService
    const inventoryItemService = inject(PROVIDER_CONSTANTS.inventoryItemService) as InventoryItemService
    const noteService = inject(PROVIDER_CONSTANTS.noteService) as NoteService
    const purchaseOrderItemService = inject(PROVIDER_CONSTANTS.purchaseOrderItemService) as PurchaseOrderItemService
    const purchaseOrderService = inject(PROVIDER_CONSTANTS.purchaseOrderService) as PurchaseOrderService
    const supplierService = inject(PROVIDER_CONSTANTS.supplierService) as SupplierService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      customerService,
      inventoryItemService,
      noteService,
      purchaseOrderItemService,
      purchaseOrderService,
      supplierService,
      toast
    }
  },
  data() {
    let customer: CustomerDetails|undefined
    let noteToEdit: Note|undefined
    let purchaseOrder: PurchaseOrderDetails|undefined
    let unchangedPurchaseOrder: PurchaseOrderDetails|undefined

    return {
      customer,
      loading: true,
      noteToEdit,
      purchaseOrder,
      saving: false,
      savingStateChange: false,
      showCreatePurchaseOrderItemModal: false,
      showNoteModal: false,
      supplierDropdownOptions: [] as DropdownOptionResponse<number>[],
      unchangedPurchaseOrder
    }
  },
  computed: {
    isDirty(): boolean {
      const a = this.purchaseOrder
      const b = this.unchangedPurchaseOrder

      return (a?.description !== b?.description ||
              a?.venderPoNumber !== b?.venderPoNumber ||
              a?.supplierId !== b?.supplierId ||
              a?.setACalendarDate !== b?.setACalendarDate) && 
              a?.supplierId !== undefined
    },
    supplierName(): string {
      if (!this.purchaseOrder) return ''

      const supplierId = this.purchaseOrder.supplierId
      const index = this.supplierDropdownOptions.findIndex(s => s.id === supplierId)
      if (index === -1) return ''

      return this.supplierDropdownOptions[index].name
    },
    titleText(): string {
      return !this.purchaseOrder || this.purchaseOrder.id === 0 ? 'Create Purchase Order' : 'Edit Purchase Order'
    }
  },
  watch: {
    '$route': {
      handler: function(): void {
        this.updatePageTitle()
      }
    }
  },
  mounted() {
    if (this.id > 0) {
      this.getSupplierDropdownOptions().then(() => {
        this.getPurchaseOrder()
      })
    } else {
      Promise.all([this.getCustomer(), this.getSupplierDropdownOptions()]).then(() => {
        this.purchaseOrder = new PurchaseOrderDetails(0)
        this.unchangedPurchaseOrder = new PurchaseOrderDetails(0)

        if (this.customerId > 0 && this.orderPartIds.length > 0 && this.customer) {
          const purchaseOrderItemFor = new PurchaseOrderItemForDetails(`${this.customer.firstName} ${this.customer.lastName}`, this.orderId)
          const purchaseOrderItems = [] as PurchaseOrderItemDetails[]
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          for (const _ of this.orderPartIds) {
            purchaseOrderItems.push(new PurchaseOrderItemDetails(0, false, 0, this.orderPartName, this.orderPartPrice, purchaseOrderItemFor, undefined, undefined))
          }

          this.purchaseOrder.purchaseOrderItems = purchaseOrderItems
        }

        this.loading = false
      })
    }
  },
  methods: {
    getCustomer(): Promise<void> {
      if (this.customerId === 0) return Promise.resolve()

      return this.customerService.getCustomer(this.customerId).then(customer => {
        this.customer = customer
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the customer. Try reloading the page.')
        }
      })
    },
    getPurchaseOrder(): Promise<void> {
      return this.purchaseOrderService.getPurchaseOrder(this.purchaseOrder ? this.purchaseOrder.id : this.id).then(purchaseOrder => {
        this.purchaseOrder = purchaseOrder
        this.unchangedPurchaseOrder = JSON.parse(JSON.stringify(purchaseOrder))
        this.updatePageTitle()
        this.loading = false
      }).catch(error => {
        this.saving = false
        this.savingStateChange = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the purchase order. Try reloading the page.')
        }
      })
    },
    getSupplierDropdownOptions(): Promise<void> {
      return this.supplierService.getSupplierDropdownOptions().then(dropdownOptionsResponse => {
        this.supplierDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the suppliers. Try reloading the page.')
        }
      })
    },
    onAddItemClicked(): void {
      this.showCreatePurchaseOrderItemModal = true
    },
    onCreateNoteClicked(): void {
      this.noteToEdit = new Note(0)
      this.showNoteModal = true
    },
    onDeleteNote(note: Note): void {
      this.noteService.deleteNote(note.id).then(() => {
        this.getPurchaseOrder().then(() => {
          this.toast.success('The note was deleted successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the note. Please try again.')
        }
      })
    },
    onDeletePurchaseOrderItemClicked(purchaseOrderItemId: number): void {
      this.purchaseOrderItemService.deletePurchaseOrderItem(purchaseOrderItemId).then(() => {
        this.getPurchaseOrder().then(() => {
          this.toast.success('The purchase order item was removed successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to remove the purchase order item. Please try again.')
        }
      })
    },
    onEditNote(note: Note): void {
      this.noteToEdit = note
      this.showNoteModal = true
    },
    onPurchaseOrderItemChanged(purchaseOrderItem: PurchaseOrderItemDetails): void {
      const updatePurchaseOrderItemMessage = new UpdatePurchaseOrderItemMessage(purchaseOrderItem.canBeUsedToFulfillOrderPart, false)
      this.purchaseOrderItemService.updatePurchaseOrderItem(purchaseOrderItem.id, updatePurchaseOrderItemMessage).then(() => {
        this.getPurchaseOrder().then(() => {
          this.toast.success('Purchase order item updated successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the purchase order item. Please try again.')
        }
      })
    },
    onReceiveAllPurchaseOrderItemsClicked(): void {
      this.savingStateChange = true
      this.purchaseOrderService.receiveAllPurchaseOrderItems(this.purchaseOrder ? this.purchaseOrder.id : this.id).then(() => {
        this.getPurchaseOrder().then(() => {
          this.toast.success('All of the purchase order items were received successfully.')
          this.savingStateChange = false
        })
      }).catch(error => {
        this.savingStateChange = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to receive all of the purchase order items. Please try again.')
        }
      })
    },
    onReceivePurchaseOrderItemClicked(purchaseOrderItem: PurchaseOrderItemDetails): void {
      const updatePurchaseOrderItemMessage = new UpdatePurchaseOrderItemMessage(purchaseOrderItem.canBeUsedToFulfillOrderPart, true)
      this.purchaseOrderItemService.updatePurchaseOrderItem(purchaseOrderItem.id, updatePurchaseOrderItemMessage).then(() => {
        this.getPurchaseOrder().then(() => {
          this.toast.success('Purchase order item received successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to receive the purchase order item. Please try again.')
        }
      })
    },
    onSavePurchaseOrderClicked(savingStateChange: boolean): void {
      if (!this.purchaseOrder || !this.purchaseOrder.supplierId) return

      if (savingStateChange)
        this.savingStateChange = true
      else
        this.saving = true

      if (this.purchaseOrder.id === 0) {
        let customerId: number|undefined
        if (this.customerId > 0)
          customerId = this.customerId

        const createPurchaseOrderMessage = new CreatePurchaseOrderMessage(customerId, this.purchaseOrder.description, this.purchaseOrder.setACalendarDate, this.purchaseOrder.supplierId, this.purchaseOrder.venderPoNumber, this.orderPartIds)
        this.purchaseOrderService.createPurchaseOrder(createPurchaseOrderMessage).then(objectCreatedResponse => {
          if (!this.purchaseOrder) return

          this.purchaseOrder.id = objectCreatedResponse.id
          this.getPurchaseOrder().then(() => {
            this.$router.replace(`/purchase-order/${objectCreatedResponse.id}`)
            this.saving = false
            this.toast.success('The purchase order was created successfully.')
          })
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to create the purchase order. Please try again.')
          }
        })
      } else {
        const updatePurchaseOrderMessage = new UpdatePurchaseOrderMessage(this.purchaseOrder.description, this.purchaseOrder.setACalendarDate, this.purchaseOrder.venderPoNumber, this.purchaseOrder.submitted, this.purchaseOrder.verified)
        this.purchaseOrderService.updatePurchaseOrder(this.id, updatePurchaseOrderMessage).then(() => {
          this.getPurchaseOrder().then(() => {
            this.toast.success('The purchase order was updated successfully.')
            this.saving = false
            this.savingStateChange = false
          })
        }).catch(error => {
          this.saving = false
          this.savingStateChange = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the purchase order. Please try again.')
          }
        })
      }
    },
    onSubmitPurchaseOrderClicked(): void {
      if (!this.purchaseOrder) return

      this.purchaseOrder.submitted = true
      this.onSavePurchaseOrderClicked(true)
    },
    onVerifyPurchaseOrderClicked(): void {
      if (!this.purchaseOrder) return

      this.purchaseOrder.verified = true
      this.onSavePurchaseOrderClicked(true)
    },
    receivedText(purchaseOrderItem: PurchaseOrderItemDetails): string {
      if (!purchaseOrderItem.receivedByUser) return ''

      return `Received by ${purchaseOrderItem.receivedByUser.firstName} ${purchaseOrderItem.receivedByUser.lastName}`
    },
    updatePageTitle(): void {
      if (this.id > 0 && this.purchaseOrder)
        document.title = `PO #${this.purchaseOrder.poNumber} - Short Action Customs, LLC`
    }
  }
})
</script>

<style scoped lang="scss">
.purchase-order-content {
  display: flex;
  flex-direction: column;
  padding: 15px;

  @include desktop {
    flex-direction: row;
  }

  > .left-content {
    display: flex;
    flex-direction: column;

    @include desktop {
      flex: 1 1 35%;
    }

    > .purchase-order-details-container {
      margin-bottom: 25px;
      position: relative;

      @include desktop {
        height: max-content;
        padding: 15px;
        background-color: $Color-fill-gray;
        border-radius: 2px;
        @include element-shadow;
      }

      > .purchase-order-details-container-header {
        grid-area: header;
        padding-bottom: 10px;
        margin-bottom: 15px;
        border-bottom: 1px solid $Color-border-gray;
        display: flex;
        flex-direction: column;

        @include desktop {
          flex-direction: row;
        }

        > .title-text {
          line-height: 37px;
          font-size: 24px;
          font-weight: bold;
        }

        > .actions {
          position: relative;
          display: flex;
          margin-left: auto;

          > .receive-all,
          > .submit-purchase-order,
          > .verify-purchase-order {
            margin-left: auto;
          }

          > .save-button {
            margin-left: 15px;
          }
        }
      }

      > .loading-spinner {
        margin: auto;
        padding: 20px;
      }

      > .purchase-order-details-container-content {
        padding-top: 15px;

        > .input {
          margin-bottom: 15px;

          &:last-of-type {
            margin-bottom: 0;
          }
        }

        > .submitted-info,
        > .verified-info {
          font-size: 12px;
          line-height: 18px;
          font-weight: normal;
          color: $Color-font-medium;
        }

        > .submitted-info {
          margin-top: 15px;
        }

        > .audit-information {
          margin-top: 0;
        }
      }
    }

    > .notes-container {
      position: relative;
      height: 100%;
      display: flex;
      flex-direction: column;
      padding-bottom: 15px;

      > .notes-container-header {
        display: flex;
        margin-bottom: 15px;

        > .notes-container-header-text {
          margin: 0 auto 0 0;
          font-size: 24px;
          font-weight: bold;
        }

        > .create-note {
          @include flat-button;
          margin: auto 0;
        }
      }

      > .loading-spinner {
        margin: auto;
      }

      > .message {
        font-style: italic;
        color: $Color-font-medium;
      }
    }
  }

  > .items-container {
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding-bottom: 15px;

    @include desktop {
      flex: 1 1 65%;
      width: 65%;
      padding-left: 20px;
    }

    > .items-container-header {
      display: flex;
      margin-bottom: 15px;

      > .items-container-header-text {
        margin: 0 auto 0 0;
        font-size: 24px;
        font-weight: bold;
      }

      > .add-item {
        @include flat-button;
        margin: auto 0;
      }
    }

    > .loading-spinner {
      margin: auto;
    }

    > .overflow-wrapper {
      overflow-y: auto;
      @include sac-table;

      .received-item {
        line-height: 24px;
      }
    }
  }

  > .slide-up {
    @include modal-animation;
  }
}
</style>
